// Apolo IDE — ConversationFeed (A2: turn grouping, estilo Claude Code).
//
// Substitui o `messages.map(<MessageBubble/>)` cru do Code tab. Segmenta a
// conversa em TURNOS e mostra o avatar/label UMA vez por turno do agente — o
// texto e os tool cards fluem embaixo, sem repetir avatar a cada mensagem. É o
// que dá a sensação "Claude Code": leitura limpa, ritmo vertical, ruído baixo.
//
// Também hospeda:
//   • Busca na conversa (C2): filtra turnos que casam com `query`.
//   • Densidade (C3): gaps confortável vs compacto via prop `density`.
//   • Navegação por teclado (C4): j/k move o turno focado, scrollIntoView + ring.
//   • Copiar turno (C1): botão no header de cada turno (hover).

import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bot, Check, Copy, Settings, AlertTriangle, Layers, Share2, Eye, RotateCcw, GitFork, ShieldCheck, Circle, MousePointer2, Paperclip } from 'lucide-react';
import { getProviderLogo, getProviderLabel } from '../../../lib/providerLogos.js';
import { parseDesignContent } from '../../../lib/designContext.js';
import { extractAttachments } from '../../../lib/attachmentSerialization.js';
import { useAuthStore } from '../../../store/useAuthStore.js';
import { useUserAvatar } from '../../../hooks/useUserAvatar.js';
import { parseChatSearchQuery, turnMatchesFilters } from '../../../lib/chatSearchQuery.js';
import { apiFetch } from '../../../lib/api.js';
import { isRunning } from '../../../lib/agentRunStore.js';
import { useAgentStore } from '../../../store/useAgentStore.js';
import MarkdownMessage from '../../MarkdownMessage.jsx';
import { dayKey, fullDate } from '../../../lib/code/sessionMeta.js';
import ToolCallBlock from './ToolCallBlock.jsx';
import AgentTodoList from './AgentTodoList.jsx';
import BlockerActions from './BlockerActions.jsx';
import WorkSummary from './WorkSummary.jsx';
import TurnFeedback from './TurnFeedback.jsx';
import ThinkingBlock from './ThinkingBlock.jsx';
import TeamPlanCard from '../team/TeamPlanCard.jsx';
import SpecReviewArtifact from './SpecReviewArtifact.jsx';
import GlassBoxDrawer from './GlassBoxDrawer.jsx';
import './redesign.css';

// v2 — realça /slash-commands (skills do Apolo) no prompt do usuário como chips
// dourados (.rx-skillchip). Sinal confiável: o /cmd explícito que o usuário
// digitou (mesma fonte do InvokedSkillsBar). Texto sem barra fica intacto.
const READ_KEY_PREFIX = 'apolo:chatRead:';

function readTurnKeys(sessionId) {
  if (!sessionId) return new Set();
  try {
    const raw = localStorage.getItem(`${READ_KEY_PREFIX}${sessionId}`);
    return new Set(JSON.parse(raw || '[]'));
  } catch {
    return new Set();
  }
}

function persistReadTurn(sessionId, turnKey, prev) {
  if (!sessionId || !turnKey || prev.has(turnKey)) return prev;
  const next = new Set(prev);
  next.add(turnKey);
  try {
    localStorage.setItem(`${READ_KEY_PREFIX}${sessionId}`, JSON.stringify([...next]));
  } catch {
    /* private mode */
  }
  return next;
}

const SkillChip = memo(function SkillChip({ cmd }) {
  return (
    <span className="rx-skillchip">
      <Layers size={11} aria-hidden="true" />
      {cmd}
    </span>
  );
});

function renderUserContent(text) {
  const str = String(text || '');
  const re = /(^|\s)(\/[a-z0-9:_-]+)/gi;
  const out = [];
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(str)) !== null) {
    const cmdStart = m.index + m[1].length;
    if (cmdStart > last) out.push(str.slice(last, cmdStart));
    out.push(<SkillChip key={`sk-${key}`} cmd={m[2]} />);
    key += 1;
    last = cmdStart + m[2].length;
  }
  if (last < str.length) out.push(str.slice(last));
  return out;
}

// Pill de Design Mode: colapsa o "textão" (seletor/estilos/HTML de um elemento
// selecionado no preview) numa etiqueta curta — igual à pill do composer.
function DesignPill({ label }) {
  return (
    <span className="rx-design-pill" title="Elemento selecionado no preview (Design Mode)">
      <MousePointer2 size={12} aria-hidden="true" />
      <code>{label}</code>
    </span>
  );
}

// Fileira de pills (um elemento selecionado por pill). Vazia → nada.
function DesignPills({ labels }) {
  if (!labels || labels.length === 0) return null;
  return (
    <span className="rx-design-pills">
      {labels.map((label, i) => <DesignPill key={`${label}-${i}`} label={label} />)}
    </span>
  );
}

// Pill de anexos: substitui o XML cru (<file …/> / "Anexos (N):") por UMA pill do
// design system ("N anexos"), com os nomes no tooltip. Pedido do Pedro: "nem
// precisa aparecer todo esse código de baixo. deixe 6 anexos como uma pill".
function AttachmentPills({ files }) {
  if (!files || files.length === 0) return null;
  const n = files.length;
  const names = files.map((f) => (f.sizeLabel ? `${f.name} (${f.sizeLabel})` : f.name)).join('\n');
  return (
    <span className="rx-attach-summary" title={names}>
      <Paperclip size={12} aria-hidden="true" />
      {n} {n > 1 ? 'anexos' : 'anexo'}
    </span>
  );
}

// Bolha do usuário (modo conversa). Quando o texto lidera com bloco(s) de Design
// Mode, mostra a(s) pill(s) + só o pedido do usuário; senão render normal (com
// chips de skill). O agente recebeu o(s) bloco(s) todo(s); aqui é só apresentação,
// e como o marcador viaja no content as pills sobrevivem ao reload.
function renderUserBubble(content) {
  const { labels, userText } = parseDesignContent(content);
  const { text: bodyText, files } = extractAttachments(userText);
  // só substitui o texto pelo "limpo" quando há anexos serializados (senão mantém
  // o comportamento original byte-a-byte).
  const body = files.length > 0 ? bodyText : userText;
  if (labels.length === 0 && files.length === 0) return renderUserContent(content);
  return (
    <>
      {labels.length > 0 && <DesignPills labels={labels} />}
      {body ? renderUserContent(body) : null}
      <AttachmentPills files={files} />
    </>
  );
}

// Render markdown de uma mensagem (usado no modo timeline / AssistantChild). Se
// for do usuário e liderar com bloco(s) de Design Mode, colapsa na(s) pill(s) + só
// o pedido (via markdown) em vez do "textão". Espelha renderUserBubble.
function MessageMarkdown({ message }) {
  if (message.role === 'user') {
    const { labels, userText } = parseDesignContent(message.content);
    const { text: bodyText, files } = extractAttachments(userText);
    const body = files.length > 0 ? bodyText : userText;
    if (labels.length > 0 || files.length > 0) {
      return (
        <div className="flex flex-col gap-1.5">
          {labels.length > 0 && <DesignPills labels={labels} />}
          {body ? <MarkdownMessage content={body} highlightSkills /> : null}
          <AttachmentPills files={files} />
        </div>
      );
    }
  }
  return <MarkdownMessage content={message.content} highlightSkills={message.role === 'user'} />;
}

function timeShort(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

// Segmenta mensagens em turnos: cada user vira um turno; sequências de
// agent/tool/system/error consecutivas viram um turno de assistente.
function segmentTurns(messages) {
  const turns = [];
  let current = null;
  for (const m of messages) {
    if (m.role === 'user') {
      turns.push({ side: 'user', key: m.id, messages: [m] });
      current = null;
    } else {
      if (!current) {
        current = { side: 'assistant', key: m.id, messages: [] };
        turns.push(current);
      }
      current.messages.push(m);
    }
  }
  return turns;
}

// Texto plano de um turno (pra copiar + casar busca).
function turnText(turn) {
  return turn.messages
    .map((m) => {
      if (m.role === 'tool') {
        const args = m.tool_args || {};
        return [m.content, args.command, args.path, args.pattern, m.result].filter(Boolean).join(' ');
      }
      return m.content || '';
    })
    .join('\n');
}

const UserBadge = memo(function UserBadge({ user }) {
  const { avatarUrl } = useUserAvatar(user);
  const initial = (user?.name || user?.email || 'P').charAt(0).toUpperCase();
  if (avatarUrl) {
    return (
      <span aria-hidden="true" className="inline-flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-apolo-surface2">
        <img src={avatarUrl} alt="" className="h-full w-full object-cover" draggable={false} />
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
      style={{ background: 'var(--rx-surface-2)', boxShadow: 'inset 0 0 0 1px var(--rx-line)', color: 'var(--rx-accent)' }}
    >
      {initial}
    </span>
  );
});

function AgentBadge({ model }) {
  const logoUrl = getProviderLogo(model);
  const [failed, setFailed] = useState(false);
  if (!logoUrl || failed) {
    return (
      <span aria-hidden="true" className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-apolo-accent/10">
        <Bot size={13} className="text-apolo-accent" aria-hidden="true" />
      </span>
    );
  }
  return (
    <span aria-hidden="true" className="inline-flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/90">
      <img src={logoUrl} alt="" width={24} height={24} loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} className="h-full w-full object-contain p-[3px]" draggable={false} />
    </span>
  );
}

function TurnCopyButton({ getText }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    const text = getText();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <button
      type="button"
      onClick={copy}
      aria-label="Copiar turno"
      title={copied ? 'Copiado!' : 'Copiar turno'}
      className="inline-flex h-6 w-6 items-center justify-center rounded-md text-apolo-textTertiary opacity-0 transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50 group-hover/turn:opacity-100"
    >
      {copied ? <Check size={12} className="text-apolo-success" aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />}
    </button>
  );
}

// Compartilhar o turno: usa a Web Share API (sheet nativo no mobile/web que o
// Pedro abre no celular); fallback pro clipboard no desktop/Electron.
function TurnShareButton({ getText }) {
  const [shared, setShared] = useState(false);
  async function share() {
    const text = getText();
    if (!text) return;
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
      }
      setShared(true);
      setTimeout(() => setShared(false), 1500);
    } catch {
      /* usuário cancelou o share sheet, ou bloqueado — silencioso */
    }
  }
  return (
    <button
      type="button"
      onClick={share}
      aria-label="Compartilhar turno"
      title={shared ? 'Compartilhado!' : 'Compartilhar'}
      className="inline-flex h-6 w-6 items-center justify-center rounded-md text-apolo-textTertiary opacity-0 transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50 group-hover/turn:opacity-100"
    >
      {shared ? <Check size={12} className="text-apolo-success" aria-hidden="true" /> : <Share2 size={12} aria-hidden="true" />}
    </button>
  );
}

// Retroceder a conversa até este turno do usuário (Cursor "restore checkpoint"):
// chama o worker, que dropa as mensagens APÓS o anchor. Pede confirmação porque
// é destrutivo. Dispara `apolo:messages-changed` pra SessionWorkspace recarregar.
function TurnRewindButton({ messageId, sessionId }) {
  const [busy, setBusy] = useState(false);
  async function rewind() {
    if (!sessionId || !messageId || busy) return;
    // Auditoria 2026-07-02 — rewind durante run ativa apagava mensagens no servidor
    // mas a tela NÃO mudava (loadMessages no-opa durante run) e o persist do fim do
    // turno regravava o histórico rebobinado por cima = perda de dados silenciosa.
    // Bloqueia com feedback claro: pare o agente antes de retroceder.
    if (isRunning(sessionId)) {
      window.dispatchEvent(new CustomEvent('apolo:toast', {
        detail: { kind: 'error', message: 'Pare o agente antes de retroceder (há um turno em execução).' },
      }));
      return;
    }
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      const okGo = window.confirm('Retroceder a conversa até este ponto?\nTudo o que veio depois será apagado.');
      if (!okGo) return;
    }
    setBusy(true);
    const res = await apiFetch(`/api/code/sessions/${sessionId}/messages/rewind`, {
      method: 'POST', body: { keep_through: messageId },
    });
    setBusy(false);
    if (res?.ok) {
      window.dispatchEvent(new CustomEvent('apolo:messages-changed', { detail: { sessionId } }));
    } else {
      window.dispatchEvent(new CustomEvent('apolo:toast', {
        detail: { kind: 'error', message: `Não foi possível retroceder: ${res?.error || 'erro desconhecido'}` },
      }));
    }
  }
  return (
    <button
      type="button"
      onClick={rewind}
      disabled={busy}
      aria-label="Retroceder até aqui"
      title="Retroceder — apaga o que veio depois"
      className="inline-flex h-6 w-6 items-center justify-center rounded-md text-apolo-textTertiary opacity-0 transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50 group-hover/turn:opacity-100 disabled:cursor-wait"
    >
      <RotateCcw size={12} aria-hidden="true" />
    </button>
  );
}

// Bifurcar a conversa daqui (Cursor "branch this conversation"): cria uma sessão
// irmã com as mensagens até este turno, copiando model/branch/etc. Pula direto
// pra ela. Leve — só clone server-side, sem worktree no disco.
function TurnForkButton({ messageId, sessionId }) {
  const setActiveSession = useAgentStore((s) => s.setActiveSession);
  const refreshSessions = useAgentStore((s) => s.refreshSessions);
  const [busy, setBusy] = useState(false);
  async function fork() {
    if (!sessionId || !messageId || busy) return;
    setBusy(true);
    const res = await apiFetch(`/api/code/sessions/${sessionId}/fork-at`, {
      method: 'POST', body: { keep_through: messageId },
    });
    setBusy(false);
    if (res?.ok && res.data?.session?.id) {
      try { await refreshSessions?.(); } catch { /* best-effort */ }
      setActiveSession?.(res.data.session.id);
      window.dispatchEvent(new CustomEvent('apolo:toast', {
        detail: { kind: 'success', message: `Bifurcado: "${res.data.session.name}"` },
      }));
    } else {
      window.dispatchEvent(new CustomEvent('apolo:toast', {
        detail: { kind: 'error', message: `Não foi possível bifurcar: ${res?.error || 'erro desconhecido'}` },
      }));
    }
  }
  return (
    <button
      type="button"
      onClick={fork}
      disabled={busy}
      aria-label="Bifurcar conversa daqui"
      title="Bifurcar — nova sessão a partir daqui"
      className="inline-flex h-6 w-6 items-center justify-center rounded-md text-apolo-textTertiary opacity-0 transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50 group-hover/turn:opacity-100 disabled:cursor-wait"
    >
      <GitFork size={12} aria-hidden="true" />
    </button>
  );
}

// Bloco interno de um turno do assistente (texto, tool, system, erro).
// E2-WS3 — memoized: `message` (stable ref except the live one) + `density`.
// A streamed token re-creates the messages ARRAY but reuses each finished
// message OBJECT, so memo skips re-rendering every prior child on each token.
// E2 (super-marathon) — the verification SEAL. Renders on the final agent bubble when a
// verification_report rode in on message.metadata (apolo_verification_report ON). HONEST
// by construction: ✓ Verificado (lists the verifiers that passed) · ○ Sem verificador
// (none ran) · ✗ Falhou (lists what failed). Render-only, no side effects, live-only.
/**
 * Render-only (not memo'd — parent turn is already memo'd; JSDoc props type keeps the
 * codetab tsc ratchet clean, which memo() would erase).
 * @param {{ report: { reachedGreen?: boolean, noVerifiers?: boolean, verifiers?: { name?: string, passed?: boolean }[] } }} props
 */
function VerificationSeal(props) {
  const report = props.report;
  if (!report || typeof report !== 'object') return null;
  const verifiers = Array.isArray(report.verifiers) ? report.verifiers : [];
  const names = (pass) => verifiers.filter((v) => !!v?.passed === pass).map((v) => v?.name).filter(Boolean);
  let Icon; let tone; let label; let detail;
  if (report.noVerifiers) {
    Icon = Circle; tone = 'text-apolo-textTertiary'; label = 'Sem verificador';
    detail = 'nenhum verificador executável rodou';
  } else if (report.reachedGreen) {
    const ok = names(true);
    Icon = ShieldCheck; tone = 'text-apolo-success'; label = 'Verificado';
    detail = ok.length ? ok.join(', ') : 'verde';
  } else {
    const bad = names(false);
    Icon = AlertTriangle; tone = 'text-apolo-error'; label = 'Falhou';
    detail = bad.length ? bad.join(', ') : 'verificação vermelha';
  }
  return (
    <div className={`mt-1.5 inline-flex items-center gap-1.5 rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface1/40 px-2 py-1 text-[11px] ${tone}`}>
      <Icon size={12} aria-hidden="true" />
      <span className="font-medium">{label}</span>
      {detail && <span className="text-apolo-textTertiary">· {detail}</span>}
    </div>
  );
}

const AssistantChild = memo(function AssistantChild({ message, density = 'comfortable' }) {
  if (message.role === 'tool') {
    // Modo Equipe (sprint subagents-team): card do plano com renderer próprio —
    // mesmo shape ao vivo e persistido (metadata), reload-safe por construção.
    if (message.tool_name === 'team_plan') return <TeamPlanCard message={message} />;
    // S3 E3b — Spec Gate review artifact (display-only; Accept/Skip via CustomEvents).
    if (message.tool_name === 'spec_review') return <SpecReviewArtifact message={message} />;
    // Cursor-style "N Tasks" checklist (E5): a tool turn carrying agent todos
    // (tool_name agent_todos / write_todos, or metadata.todos) renders the live
    // checklist that ticks off as the agent works.
    const todos = message.metadata?.todos || message.todos;
    if (
      (message.tool_name === 'agent_todos' || message.tool_name === 'write_todos' || Array.isArray(todos)) &&
      Array.isArray(todos)
    ) {
      return <AgentTodoList todos={todos} />;
    }
    return <ToolCallBlock message={message} density={density} />;
  }
  if (message.role === 'system' || message.role === 'error') {
    const isError = message.role === 'error';
    const Icon = isError ? AlertTriangle : Settings;
    return (
      <div className={`flex items-start gap-2 rounded-apolo-md px-3 py-1.5 text-[12px] ${isError ? 'bg-apolo-error/[0.05] text-apolo-error' : 'bg-apolo-surface1/40 text-apolo-textTertiary'}`}>
        <Icon size={13} className="mt-0.5 shrink-0" aria-hidden="true" />
        <span className="min-w-0">
          {message.content}
          <BlockerActions metadata={message.metadata} />
        </span>
      </div>
    );
  }
  // agent / assistant text. Mostra o raciocínio (ThinkingBlock) acima e, durante
  // o streaming, o texto cru + cursor (markdown parcial fica feio mid-stream);
  // ao finalizar, renderiza o markdown completo. Mensagens do usuário realçam
  // /skills (azul); texto do agente não (evita pintar paths nas respostas).
  // HRN3-T01 — live messages carry `reasoning` directly; reloaded ones carry a
  // persisted summary in metadata.reasoning. Read both so the ThinkingBlock
  // survives a reload instead of vanishing.
  const reasoning = typeof message.reasoning === 'string' && message.reasoning
    ? message.reasoning
    : (typeof message.metadata?.reasoning === 'string' ? message.metadata.reasoning : '');
  const streaming = !!message.streaming;
  // E2 — the seal rides on the final bubble's metadata; render once the turn is done.
  const seal = message.metadata?.verificationReport
    ? <VerificationSeal report={message.metadata.verificationReport} />
    : null;
  if (reasoning || streaming) {
    return (
      <div className="flex flex-col gap-1">
        {reasoning && (
          <ThinkingBlock
            content={reasoning}
            finished={!streaming}
            tokens={streaming ? 0 : Math.round(reasoning.length / 4)}
          />
        )}
        {streaming ? (
          message.content ? (
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-apolo-textPrimary">
              {message.content}
              <span
                aria-hidden="true"
                className="ml-0.5 inline-block h-3.5 w-[3px] translate-y-[2px] animate-pulse rounded-sm bg-apolo-accent"
              />
            </div>
          ) : null
        ) : (
          <MarkdownMessage content={message.content} highlightSkills={false} />
        )}
        {!streaming && seal}
      </div>
    );
  }
  if (seal) {
    return (
      <div className="flex flex-col gap-1">
        <MessageMarkdown message={message} />
        {seal}
      </div>
    );
  }
  return <MessageMarkdown message={message} />;
});

// Corpo de um turno do assistente. Runs de ≥2 tools consecutivos colapsam num
// WorkSummary ("Trabalhou por Xs"); 1 tool, texto, system ou erro renderiza
// inline. WorkSummary abre por padrão se algum tool do run falhou/foi negado. (E4)
function TurnBody({ messages, blockGap, density = 'comfortable' }) {
  const nodes = [];
  let run = [];
  const flush = () => {
    if (run.length === 0) return;
    const group = run;
    run = [];
    if (group.length === 1) {
      nodes.push(<AssistantChild key={group[0].id} message={group[0]} density={density} />);
      return;
    }
    const totalMs = group.reduce((s, m) => s + (m.duration_ms || 0), 0) || null;
    const hasIssue = group.some(
      (m) =>
        m.status === 'denied' ||
        m.status === 'aborted' ||
        /^exitCode:\s*(?!0\b)-?\d/.test(String(m.result || '')) ||
        /^Erro/i.test(String(m.result || '')),
    );
    nodes.push(
      <WorkSummary key={`ws-${group[0].id}`} durationMs={totalMs} actionCount={group.length} defaultOpen={density === 'detailed' || (hasIssue && density !== 'compact')}>
        <div className={`flex flex-col ${blockGap}`}>
          {group.map((m) => (
            <AssistantChild key={m.id} message={m} density={density} />
          ))}
        </div>
      </WorkSummary>,
    );
  };
  for (const m of messages) {
    if (m.role === 'tool') run.push(m);
    else {
      flush();
      nodes.push(<AssistantChild key={m.id} message={m} density={density} />);
    }
  }
  flush();
  return <>{nodes}</>;
}

// perf/RAM — cap how many recent turns render at once in a long session; older
// turns mount on demand via the "show earlier" control. Generous enough that a
// normal conversation is never windowed.
const RENDER_WINDOW = 100;

export default function ConversationFeed({ messages, model, density = 'comfortable', query = '', sessionId, render = 'conversa' }) {
  const user = useAuthStore((s) => s.user);
  const turns = useMemo(() => segmentTurns(messages || []), [messages]);

  const q = query.trim();
  const searchFilters = useMemo(() => parseChatSearchQuery(q), [q]);
  const visibleTurns = useMemo(() => {
    if (!q) return turns;
    return turns.filter((t) => turnMatchesFilters(t, searchFilters, turnText));
  }, [turns, q, searchFilters]);

  // perf/RAM — windowing. A very long session renders only the most recent
  // RENDER_WINDOW turns so the DOM node count stays bounded (less memory, no
  // scroll jank). The newest turns are ALWAYS shown, so streaming and
  // stick-to-bottom are unaffected; the "show earlier" control reveals the
  // rest. During a search we never window — every match must be reachable.
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { setShowAll(false); }, [sessionId]);
  const windowed = !q && !showAll && visibleTurns.length > RENDER_WINDOW;
  const renderedTurns = windowed ? visibleTurns.slice(-RENDER_WINDOW) : visibleTurns;
  const hiddenCount = windowed ? visibleTurns.length - RENDER_WINDOW : 0;

  const [readKeys, setReadKeys] = useState(() => readTurnKeys(sessionId));
  const readKeysRef = useRef(readKeys);
  readKeysRef.current = readKeys;
  useEffect(() => { setReadKeys(readTurnKeys(sessionId)); }, [sessionId]);

  // Marca turnos como lidos quando entram na viewport.
  // E5-WS2-T01 (FASE-11) — deps ESTÁVEIS: a assinatura muda só quando o CONJUNTO
  // de turnos renderizados muda (turno novo / janela / sessão) — NÃO a cada delta
  // de token. Antes a dep era `renderedTurns` (identidade nova por delta) → o
  // observer era destruído+recriado+re-observava todos os turnos POR TOKEN.
  const turnKeySig = renderedTurns.map((t) => t.key).join('|');
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        let batch = readKeysRef.current;
        let changed = false;
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.45) continue;
          const key = entry.target.getAttribute('data-turn-key');
          if (!key || batch.has(key)) continue;
          batch = persistReadTurn(sessionId, key, batch);
          changed = true;
        }
        if (changed) setReadKeys(new Set(batch));
      },
      { root: null, threshold: [0.45, 0.6] },
    );
    turnRefs.current.forEach((el) => { if (el) io.observe(el); });
    return () => io.disconnect();
  }, [turnKeySig, sessionId]);

  // Navegação por teclado (j/k): move o turno focado entre os visíveis.
  const [focused, setFocused] = useState(-1);
  const [glassOpen, setGlassOpen] = useState(false); // E2 — Glass Box drawer (session-scoped prompt + craft)
  const turnRefs = useRef([]);
  const rootRef = useRef(null);

  useEffect(() => { setFocused(-1); }, [q]);

  const onKeyDown = useCallback((e) => {
    // Ignora quando o foco está num input/textarea/botão real.
    const tag = (e.target.tagName || '').toLowerCase();
    if (tag === 'textarea' || tag === 'input' || e.target.isContentEditable) return;
    const max = renderedTurns.length - 1;
    if (e.key === 'j' || e.key === 'ArrowDown') {
      e.preventDefault();
      setFocused((i) => Math.min(max, i + 1));
    } else if (e.key === 'k' || e.key === 'ArrowUp') {
      e.preventDefault();
      setFocused((i) => Math.max(0, (i < 0 ? max + 1 : i) - 1));
    } else if (e.key === 'Enter' && focused >= 0) {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('apolo:focusComposer'));
    } else if (e.key === 'Escape') {
      setFocused(-1);
    }
  }, [renderedTurns.length, focused]);

  useEffect(() => {
    if (focused >= 0 && turnRefs.current[focused]) {
      turnRefs.current[focused].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [focused]);

  // E7 — modo timeline: turnos viram nós numa espinha vertical (mesma lógica de
  // turnos/TurnBody/feedback, só muda o invólucro). gap-0 + border-l por turno
  // = espinha contínua sem pixel-math frágil.
  const timeline = render === 'timeline';
  const gap = timeline ? 'gap-0' : density === 'compact' ? 'gap-1' : 'gap-5';
  const blockGap = density === 'compact' ? 'gap-0.5' : 'gap-2';
  const bodyPad = density === 'compact' ? 'pl-0' : 'pl-8';

  if (q && visibleTurns.length === 0) {
    return <p className="px-3 py-6 text-center text-sm text-apolo-textTertiary">Nenhum turno casa com “{query}”.</p>;
  }

  return (
    <div
      ref={rootRef}
      tabIndex={0}
      role="list"
      onKeyDown={onKeyDown}
      aria-label={`${timeline ? 'Timeline' : 'Conversa'} — j/k navega, Enter foca resposta`}
      data-density={density}
      data-render={render}
      className={`workbench-conversation mx-auto flex w-full max-w-[var(--chat-col,76rem)] flex-col ${gap} outline-none ${timeline ? 'py-1' : density === 'compact' ? 'py-0' : ''}`}
    >
      {hiddenCount > 0 && (
        <div role="listitem" className="flex justify-center">
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="my-1 rounded-md px-3 py-1.5 text-xs text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50"
          >
            ↑ Mostrar mensagens anteriores ({hiddenCount})
          </button>
        </div>
      )}
      {renderedTurns.map((turn, idx) => {
        const isUser = turn.side === 'user';
        const turnModel = turn.messages.find((m) => m.role === 'agent')?.model || model;
        const label = isUser
          ? (user?.name || user?.email?.split('@')[0] || 'Você')
          : (turnModel ? getProviderLabel(turnModel) : 'Agente');
        const ts = timeShort(turn.messages[0]?.created_at);
        // Divisor de DATA dentro do chat (estilo claude): aparece quando o dia do
        // turno difere do turno anterior. Só na conversa normal (a timeline já tem
        // a própria espinha temporal).
        const dayK = dayKey(turn.messages[0]?.created_at);
        const prevDayK = idx > 0 ? dayKey(renderedTurns[idx - 1].messages[0]?.created_at) : null;
        const dateDivider = (!timeline && dayK && dayK !== prevDayK) ? (
          <div role="separator" aria-label={fullDate(turn.messages[0]?.created_at)} className="workbench-date-divider my-2 flex items-center gap-3 px-1">
            <span aria-hidden="true" className="h-px flex-1 bg-apolo-borderSubtle" />
            <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-apolo-textTertiary">{fullDate(turn.messages[0]?.created_at)}</span>
            <span aria-hidden="true" className="h-px flex-1 bg-apolo-borderSubtle" />
          </div>
        ) : null;
        const ringed = idx === focused;
        const isRead = readKeys.has(turn.key);
        const turnClass = [
          'group/turn rounded-apolo-lg transition-shadow',
          ringed ? 'rx-turn-focused ring-2 ring-apolo-accent/40' : '',
          isRead ? 'rx-turn-read' : '',
        ].filter(Boolean).join(' ');
        // v2 — turno do usuário vira uma bolha alinhada à direita (estilo Cursor/Codex):
        // sem avatar/label, o prompt fala por si. Ações ficam ABAIXO da bolha (Copiar,
        // Retroceder, Bifurcar) — Cursor-style row, opacity-0 → hover. Mantém o listitem
        // (j/k continua navegando). Timeline conserva o nó.
        if (isUser && !timeline) {
          const firstMsg = turn.messages[0];
          return (
            <Fragment key={turn.key}>
              {dateDivider}
              <div
                ref={(el) => { turnRefs.current[idx] = el; }}
                role="listitem"
                data-turn-key={turn.key}
                className={`rx-userwrap rx-userwrap-stack ${turnClass}`}
              >
                <div className="rx-userbubble" style={{ whiteSpace: 'pre-wrap' }}>
                  {renderUserBubble(firstMsg?.content)}
                </div>
                <div className="rx-useractions">
                  <TurnCopyButton getText={() => firstMsg?.content || ''} />
                  {sessionId && firstMsg?.id && (
                    <>
                      <TurnRewindButton sessionId={sessionId} messageId={firstMsg.id} />
                      <TurnForkButton sessionId={sessionId} messageId={firstMsg.id} />
                    </>
                  )}
                </div>
              </div>
            </Fragment>
          );
        }
        return (
          <Fragment key={turn.key}>
          {dateDivider}
          <div
            ref={(el) => { turnRefs.current[idx] = el; }}
            role="listitem"
            data-turn-key={turn.key}
            data-side={turn.side}
            className={`${turnClass} flex flex-col ${timeline ? 'relative ml-1.5 gap-1.5 border-l-2 border-apolo-borderSubtle pb-4 pl-5' : density === 'compact' ? 'gap-0.5 px-0 py-0' : 'gap-1.5 px-1 py-0.5'}`}
          >
            {/* Cursor parity — nó na espinha da timeline: user = accent (laranja),
               agente = neutro mudo (Cursor não pinta o turno do agente de azul). */}
            {timeline && (
              <span
                aria-hidden="true"
                className={`absolute -left-[7px] top-1 h-3 w-3 rounded-full ring-2 ring-apolo-bg ${isUser ? 'bg-apolo-accent' : 'bg-apolo-textTertiary'}`}
              />
            )}
            {/* Header do turno — avatar + label + tempo + copiar (uma vez por turno) */}
            <div className={`workbench-message-heading flex items-center gap-2 ${density === 'compact' ? 'min-h-0 py-0' : ''}`}>
              <span className="rx-read-dot" aria-hidden="true" title="Turno lido" />
              {density !== 'compact' && (isUser ? <UserBadge user={user} /> : <AgentBadge model={turnModel} />)}
              <span className={`workbench-message-author font-semibold ${density === 'compact' ? 'text-[11px]' : 'text-[12px]'} ${isUser ? 'text-apolo-textPrimary' : 'text-apolo-accent'}`}>
                {label}
              </span>
              {ts && density !== 'compact' && (
                <time className="text-[10px] tabular-nums text-apolo-textTertiary opacity-0 transition-opacity group-hover/turn:opacity-100 motion-reduce:transition-none">
                  {ts}
                </time>
              )}
              <span className="ml-auto inline-flex items-center gap-0.5">
                {!isUser && sessionId && (
                  <button
                    type="button"
                    onClick={() => setGlassOpen(true)}
                    aria-label="Ver o prompt deste turno (Glass Box)"
                    title="Glass Box — o que o modelo recebeu"
                    className="rounded-apolo-md p-1 text-apolo-textTertiary opacity-0 outline-none transition-opacity hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-apolo-accent group-hover/turn:opacity-100 motion-reduce:transition-none"
                  >
                    <Eye size={13} aria-hidden="true" />
                  </button>
                )}
                <TurnCopyButton getText={() => turnText(turn)} />
                <TurnShareButton getText={() => turnText(turn)} />
              </span>
            </div>
            {/* Corpo do turno — indentado sob o avatar. Tool runs (≥2) colapsam
                num "Trabalhou por Xs" (E4); footer do assistente tem 👍/👎 (E6). */}
            <div className={`rx-turn-body flex flex-col ${blockGap} ${bodyPad}`}>
              <TurnBody messages={turn.messages} blockGap={blockGap} density={density} />
              {!isUser && sessionId && (
                <div className="pt-0.5 opacity-0 transition-opacity group-hover/turn:opacity-100 focus-within:opacity-100">
                  <TurnFeedback sessionId={sessionId} turnId={turn.key} />
                </div>
              )}
            </div>
          </div>
          </Fragment>
        );
      })}
      <GlassBoxDrawer sessionId={sessionId} open={glassOpen} onClose={() => setGlassOpen(false)} />
    </div>
  );
}
